/**
 * Custom hook that provides API configuration with branch context
 */

import { useContext, useMemo } from 'react';
import { AuthContext } from '../contexts/user-context/user-context';
import { useSelectedBranch } from '../contexts/selected-branch-context';
import { getConfigWithBranch } from '../utils/branch-api-config';

/**
 * Hook that provides axios configuration with branch context header
 * Combines the AuthContext config with the selected branch from SelectedBranchContext
 * 
 * @returns Object with config (includes auth and branch headers) and selectedBranch
 * 
 * @example
 * const { config, selectedBranch, branchKey } = useApiConfigWithBranch();
 * 
 * // Use branchKey in useEffect to trigger refetch on branch change
 * useEffect(() => {
 *   fetchData();
 * }, [branchKey]);
 * 
 * const response = await axios.get('/api/v1/orders/', config);
 */
export function useApiConfigWithBranch() {
  const { config: authConfig } = useContext(AuthContext);
  const { selectedBranch } = useSelectedBranch();

  // Memoize config to prevent creating new object references on every render
  const config = useMemo(() => getConfigWithBranch(authConfig, selectedBranch), [authConfig, selectedBranch]);

  return {
    config,
    selectedBranch,
    hasBranchContext: !!selectedBranch,
    // branchKey changes when branch changes, useful for useEffect dependencies
    branchKey: selectedBranch?.id || 'no-branch',
  };
}

